import { useMemo } from "react";
import {
  DataTable,
  GridStatus,
  LinkButton,
  Tag,
  cx,
  dataColumns,
  useAppNavigate,
  useSortParam,
  type DataColumn,
  type DataColumnMeta,
  type DataGroup,
} from "@amber/ui";
import type { ItemRow, ItemsResponse, StatusGroup } from "../../contracts/index";
import { BacklogCell } from "./BacklogCell";
import { COLUMNS, GROUPS, GROUP_OF, type Column } from "./columns";
import type { BacklogSelection } from "./useBacklogSelection";

const col = dataColumns<ItemRow>();

function TitleCell({ row }: { row: ItemRow }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span
        className={cx("truncate font-strong", row.status.status === "excluded" ? "text-muted" : "text-fg")}
        title={row.title}
      >
        {row.title}
      </span>
      {row.added_by_link ? <Tag tone="quiet">Added by link</Tag> : null}
      {row.access_state !== "normal" ? (
        <Tag tone="attention">{row.access_state === "restricted" ? "Restricted" : "Deleted"}</Tag>
      ) : null}
    </span>
  );
}

function metaOf(c: Column): DataColumnMeta {
  const interactive = c.id === "status" || c.id === "readiness";
  return {
    label: c.label,
    width: c.width,
    ...("sort" in c ? { sort: c.sort } : {}),
    ...(interactive ? { interactive, cellClassName: "overflow-visible" } : {}),
  };
}

function backlogColumns(narrow: boolean): readonly DataColumn<ItemRow>[] {
  return [
    col.display({
      id: "title",
      header: "Title",
      meta: { label: "Title", sort: "title", width: narrow ? 240 : 320 },
      cell: ({ row }) => <TitleCell row={row.original} />,
    }),
    ...COLUMNS.map((c) =>
      col.display({
        id: c.id,
        header: c.label,
        meta: metaOf(c),
        cell: ({ row }) => <BacklogCell column={c.id} item={row.original} />,
      }),
    ),
  ];
}

const WIDE_COLUMNS = backlogColumns(false);
const NARROW_COLUMNS = backlogColumns(true);
const NOT_NARROW: readonly string[] = COLUMNS.filter((c) => !c.narrow).map((c) => c.id);
const PINNED = ["title"];

export function BacklogTable({
  data,
  isPending,
  grouped,
  hidden,
  widthsReset,
  narrow,
  collapsed,
  onToggleGroup,
  selection,
  itemId,
  sort,
  onSort,
  onReset,
  limit,
  onMore,
  onOpenItem,
}: {
  data: ItemsResponse | undefined;
  isPending: boolean;
  grouped: boolean;
  hidden: ReadonlySet<string>;
  widthsReset: number;
  narrow: boolean;
  collapsed: ReadonlySet<StatusGroup>;
  onToggleGroup: (key: StatusGroup, isCollapsed: boolean) => void;
  selection: BacklogSelection;
  itemId: string | null;
  sort: string | undefined;
  onSort: (sort: string | undefined) => void;
  onReset: () => void;
  limit: number;
  onMore: () => void;
  onOpenItem: (id: string | null) => void;
}) {
  const openRecord = useAppNavigate();
  const sortParam = useSortParam(sort, onSort);
  const rows = data?.rows ?? [];
  const shownHidden = useMemo(() => (narrow ? new Set([...hidden, ...NOT_NARROW]) : hidden), [narrow, hidden]);
  const groups: DataGroup<ItemRow>[] | null = grouped
    ? GROUPS.map((g) => {
        const isCollapsed = collapsed.has(g.key);
        return {
          key: g.key,
          label: g.label,
          note: g.note,
          rows: rows.filter((r) => GROUP_OF[r.status.status] === g.key),
          count: data?.group_counts[g.key] ?? 0,
          collapsed: isCollapsed,
          attention: g.key === "needs_attention",
          onToggle: () => {
            onToggleGroup(g.key, isCollapsed);
          },
        };
      }).filter((g) => g.count > 0)
    : null;
  return (
    <DataTable
      label="Items"
      columns={narrow ? NARROW_COLUMNS : WIDE_COLUMNS}
      rows={rows}
      rowId={rowId}
      rowCount={data?.total}
      minWidth={narrow ? 560 : 1500}
      pinned={PINNED}
      hidden={shownHidden}
      widthsReset={widthsReset}
      sort={sortParam}
      selection={{
        picked: selection.checked,
        onToggle: selection.toggle,
        onToggleLoaded: selection.toggleLoaded,
        rowLabel: titleOf,
      }}
      groups={groups}
      selectedId={itemId}
      isPending={isPending}
      rowOptions={(row) => ({
        label: `${row.title}, ${row.status.label}`,
        muted: row.status.status === "excluded",
        onOpen: () => {
          onOpenItem(row.id);
        },
        onActivate: () => {
          openRecord(`/items/${row.id}?from=backlog`);
        },
      })}
      empty={
        data ? (
          <>
            No items match these filters. <LinkButton onClick={onReset}>Reset all filters</LinkButton>
          </>
        ) : undefined
      }
      after={data ? <GridStatus shown={rows.length} total={data.total} onMore={limit < 200 ? onMore : null} /> : null}
    />
  );
}

function rowId(row: ItemRow): string {
  return row.id;
}

function titleOf(row: ItemRow): string {
  return row.title;
}
